import { Component } from '@theme/component';
import {
  prefersReducedMotion,
  debounce,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLElement} gallery - The horizontal full-screen gallery.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails', 'gallery'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #highResImagePromises = /** @type {Map<string, Promise<HTMLImageElement>>} */ (new Map());
  #activeIndex = 0;
  #openRequest = 0;

  connectedCallback() {
    super.connectedCallback();
    this.refs.gallery.addEventListener('scroll', this.handleScroll);
    this.refs.gallery.addEventListener('mousedown', this.#handleMouseDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.gallery.removeEventListener('scroll', this.handleScroll);
    this.refs.gallery.removeEventListener('mousedown', this.#handleMouseDown);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  open(index, event) {
    event?.preventDefault();

    const { dialog } = this.refs;
    const request = ++this.#openRequest;
    this.#activeIndex = index;

    // Prime the dialog synchronously from the already visible carousel image.
    // This keeps showModal in the mobile tap call stack without reintroducing
    // the blank/preview flash that the deferred zoom images used to cause.
    this.#primePreviewImage(index);
    if (!dialog.open) dialog.showModal();
    this.selectThumbnail(index, { behavior: 'instant', previewReady: true });

    this.#preparePreviewImage(index).then(() => {
      if (request !== this.#openRequest || !dialog.open) return;
      this.#prepareAdjacentPreviews(index);
    });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  async loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    let image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl) return false;
    if (image.dataset.velaHighResolution === highResolutionUrl) return true;

    let imagePromise = this.#highResImagePromises.get(highResolutionUrl);
    if (!imagePromise) {
      imagePromise = this.#loadAndDecodeImage(highResolutionUrl);
      this.#highResImagePromises.set(highResolutionUrl, imagePromise);
    }

    try {
      const decodedImage = await imagePromise;
      image = mediaContainer.querySelector('img.product-media__image');
      if (!image || !(image instanceof HTMLImageElement)) return false;
      if (image.dataset.velaHighResolution === highResolutionUrl) return true;

      decodedImage.className = image.className;
      decodedImage.alt = image.alt;
      if (image.hasAttribute('width')) decodedImage.setAttribute('width', image.getAttribute('width'));
      if (image.hasAttribute('height')) decodedImage.setAttribute('height', image.getAttribute('height'));
      decodedImage.style.cssText = image.style.cssText;
      decodedImage.fetchPriority = 'high';
      decodedImage.draggable = false;
      decodedImage.setAttribute('data_max_resolution', highResolutionUrl);
      decodedImage.setAttribute('data-vela-high-resolution', highResolutionUrl);
      decodedImage.setAttribute('ref', 'image');

      // The replacement is already decoded, so the browser can paint it in
      // the same frame instead of briefly clearing the existing preview.
      image.replaceWith(decodedImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
      return true;
    } catch (error) {
      this.#highResImagePromises.delete(highResolutionUrl);
      return false;
    }
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails, gallery } = this.refs;

    const mostVisibleElement = getMostVisibleElement(media, gallery);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (targetThumbnail instanceof HTMLElement) {
      Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
        button.setAttribute('aria-current', `${i === activeIndex}`);
      });
    }

    this.#activeIndex = activeIndex;
    await this.#preparePreviewImage(activeIndex);
    this.#prepareAdjacentPreviews(activeIndex);
    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  close() {
    this.closeDialog();
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'ArrowLeft') {
      this.previous(event);
    } else if (event.key === 'ArrowRight') {
      this.next(event);
    }
  }

  /** Selects the previous full-screen image and wraps at the start. */
  previous(event) {
    event?.preventDefault();
    const count = this.refs.media.length;
    if (!count) return;
    this.selectThumbnail((this.#activeIndex - 1 + count) % count);
  }

  /** Selects the next full-screen image and wraps at the end. */
  next(event) {
    event?.preventDefault();
    const count = this.refs.media.length;
    if (!count) return;
    this.selectThumbnail((this.#activeIndex + 1) % count);
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    await this.#preparePreviewImage(index);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.media.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.media.length) return;

    const { media, thumbnails, gallery } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!options.previewReady) await this.#preparePreviewImage(index);
    this.#prepareAdjacentPreviews(index);

    if (targetThumbnail instanceof HTMLElement) {
      Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
        button.setAttribute('aria-current', `${i === index}`);
      });
    }

    if (targetThumbnail instanceof HTMLElement && thumbnails.getClientRects().length) {
      scrollIntoView(targetThumbnail, {
        ancestor: thumbnails,
        behavior: options.behavior,
        block: 'center',
        inline: 'center',
      });
    }

    const targetImage = media[index];

    if (targetImage) {
      gallery.scrollTo({
        left: targetImage.offsetLeft,
        behavior: options.behavior,
      });

      this.#activeIndex = index;
      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  async #preparePreviewImage(index) {
    const mediaContainer = this.refs.media[index];
    if (!mediaContainer?.classList.contains('product-media-container--image')) return false;

    const dialogImage = mediaContainer.querySelector('img.product-media__image');
    if (!dialogImage || !(dialogImage instanceof HTMLImageElement)) return false;
    if (dialogImage.dataset.velaHighResolution) return true;

    const previewReady = this.#primePreviewImage(index);
    if (!previewReady) return false;

    try {
      await dialogImage.decode();
    } catch (error) {
      // The primed source is already usable even if decode is unsupported or
      // interrupted by a responsive-source update.
    }
    return true;
  }

  #primePreviewImage(index) {
    const mediaContainer = this.refs.media[index];
    if (!mediaContainer?.classList.contains('product-media-container--image')) return false;

    const dialogImage = mediaContainer.querySelector('img.product-media__image');
    if (!dialogImage || !(dialogImage instanceof HTMLImageElement)) return false;
    if (dialogImage.dataset.velaHighResolution) return true;

    const mediaGallery = this.closest('media-gallery');
    const productCarousel = mediaGallery?.querySelector('vela-product-carousel');
    productCarousel?.prepareIndex?.(index, { includeAdjacent: false });
    const carouselImage = productCarousel?.getImage?.(index);

    if (carouselImage instanceof HTMLImageElement) {
      const previewUrl = carouselImage.currentSrc || carouselImage.src;
      if (previewUrl && !previewUrl.startsWith('data:image/')) {
        dialogImage.removeAttribute('srcset');
        dialogImage.src = previewUrl;
        dialogImage.fetchPriority = 'high';
        dialogImage.removeAttribute('data-src');
        dialogImage.removeAttribute('data-srcset');
        dialogImage.removeAttribute('data-vela-deferred-image');
        dialogImage.removeAttribute('data-vela-zoom-placeholder');
        return true;
      }
    }

    // Fallback for galleries that do not use VelaProductCarousel.
    const src = dialogImage.dataset.src;
    const srcset = dialogImage.dataset.srcset;
    dialogImage.loading = 'eager';
    dialogImage.fetchPriority = 'high';
    if (srcset) dialogImage.srcset = srcset;
    if (src) dialogImage.src = src;
    dialogImage.removeAttribute('data-src');
    dialogImage.removeAttribute('data-srcset');
    dialogImage.removeAttribute('data-vela-deferred-image');
    dialogImage.removeAttribute('data-vela-zoom-placeholder');
    return true;
  }

  #prepareAdjacentPreviews(index) {
    const count = this.refs.media.length;
    if (count <= 1) return;
    const previousIndex = (index - 1 + count) % count;
    const nextIndex = (index + 1) % count;
    this.#preparePreviewImage(previousIndex);
    this.#preparePreviewImage(nextIndex);
  }

  #loadAndDecodeImage(url) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.decoding = 'async';
      image.fetchPriority = 'high';
      image.onload = async () => {
        try {
          await image.decode();
          resolve(image);
        } catch (error) {
          reject(error);
        }
      };
      image.onerror = reject;
      image.src = url;
    });
  }

  /**
   * Adds grab-to-slide behavior for desktop pointers. Touch devices keep
   * native horizontal scrolling so pinch zoom can coexist with swiping.
   * @param {MouseEvent} event
   */
  #handleMouseDown = (event) => {
    if (event.button !== 0 || this.refs.media.length <= 1) return;
    if (!(event.target instanceof Element) || event.target.closest('button, model-viewer, video')) return;

    const { gallery, media } = this.refs;
    const startX = event.clientX;
    const startScrollLeft = gallery.scrollLeft;
    const controller = new AbortController();
    const { signal } = controller;
    let moved = false;

    event.preventDefault();
    gallery.classList.add('is-dragging');

    const onMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      if (Math.abs(delta) > 4) moved = true;
      gallery.scrollLeft = startScrollLeft - delta;
    };

    const onMouseUp = (upEvent) => {
      controller.abort();
      gallery.classList.remove('is-dragging');

      const mostVisibleElement = getMostVisibleElement(media, gallery);
      const actualIndex = media.indexOf(mostVisibleElement);
      this.selectThumbnail(actualIndex >= 0 ? actualIndex : this.#activeIndex);
    };

    document.addEventListener('mousemove', onMouseMove, { signal });
    document.addEventListener('mouseup', onMouseUp, { signal, once: true });
  };
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @param {HTMLElement} scrollContainer - The horizontal scroll container.
 * @returns {HTMLElement} The most visible element.
 */
function getMostVisibleElement(elements, scrollContainer) {
  const referencePoint = scrollContainer.scrollLeft + scrollContainer.clientWidth / 2;

  return elements.reduce((closestElement, element) => {
    const elementCenter = element.offsetLeft + element.offsetWidth / 2;
    const closestCenter = closestElement.offsetLeft + closestElement.offsetWidth / 2;
    return Math.abs(elementCenter - referencePoint) < Math.abs(closestCenter - referencePoint)
      ? element
      : closestElement;
  }, elements[0]);
}
